import useGlobalLoading from '@/composables/useGlobalLoading';
import useNavigateTo401 from '@/composables/useNavigateTo401';
import useOidc from '@/composables/useOidc';
import useProfile from '@/composables/useProfile';
import Constants, { IOT_ADMIN, IOT_AUTHORITY, IOT_READ_ONLY } from '@/constants';
import { watch } from '@vue/composition-api';
import CommonHelper from '@/utilities/common.helper';
import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: ''
  },
  {
    path: '/401',
    name: '401',
    component: () => import('@/views/401.vue'),
    props: true
  },
  {
    path: '/devices',
    name: 'Enheder',
    component: () => import('@/views/Devices/Devices.vue'),
    meta: {
      includeInNavigationBar: true,
      localizedName: 'routes.devices',
      roleRequirements: [IOT_ADMIN, IOT_AUTHORITY, IOT_READ_ONLY]
    },
    children: [
      {
        path: ':organisationId/new',
        name: 'NewDevice',
        props: true,
        meta: {
          needsParent: true,
          roleRequirements: [IOT_ADMIN, IOT_AUTHORITY]
        },
        component: () => import('@/views/Devices/Device.vue')
      },
      {
        path: ':deviceId',
        name: 'Device',
        props: true,
        meta: {
          needsParent: true,
          roleRequirements: [IOT_ADMIN, IOT_AUTHORITY, IOT_READ_ONLY],
          localizedName: 'routes.devices'
        },
        component: () => import('@/views/Devices/Device.vue')
      }
    ]
  },
  {
    path: '/templates',
    name: 'Skabeloner',
    component: () => import('@/views/Templates/Templates.vue'),
    meta: {
      includeInNavigationBar: true,
      localizedName: 'routes.templates',
      roleRequirements: [IOT_ADMIN]
    },
    children: [
      {
        path: 'new',
        name: 'NewTemplate',
        props: true,
        meta: {
          needsParent: true,
          roleRequirements: [IOT_ADMIN]
        },
        component: () => import('@/views/Templates/Template.vue')
      },
      {
        path: ':id',
        name: 'Template',
        props: true,
        meta: {
          needsParent: true,
          roleRequirements: [IOT_ADMIN],
          localizedName: 'routes.templates'
        },
        component: () => import('@/views/Templates/Template.vue')
      }
    ]
  },
  {
    path: '/authorities',
    name: 'Myndigheder',
    component: () => import('@/views/Authorities/Authorities.vue'),
    meta: {
      includeInNavigationBar: true,
      localizedName: 'routes.authorities',
      roleRequirements: [IOT_ADMIN]
    },
    children: [
      {
        path: 'new',
        name: 'NewAuthority',
        props: true,
        meta: {
          needsParent: true,
          roleRequirements: [IOT_ADMIN],
          localizedName: 'routes.authorities'
        },
        component: () => import('@/views/Authorities/Authority.vue')
      },
      {
        path: ':id',
        name: 'Authority',
        props: true,
        meta: {
          needsParent: true,
          roleRequirements: [IOT_ADMIN]
        },
        component: () => import('@/views/Authorities/Authority.vue')
      }
    ]
  },
  {
    path: '/claims',
    name: 'Claims',
    component: () => import('@/views/Claims.vue'),
    meta: {
      localizedName: 'common.claims'
    }
  }
];

const router = new VueRouter({
  routes,
  mode: 'history',
  base: process.env.BASE_URL
});

const { profile, roles, isReadOnly } = useProfile();

router.beforeEach(async (to, from, next) => {
  if (to.path === '/') {
    return next({ path: '/devices' });
  }
  // When oidc has been initialized, and is logged in, we want to ensure that the user is downloaded from the graph before continuing.
  if (useOidc().user.value != null && from.path != Constants.oidc_callback_uri) {
    if (profile.value == null) useGlobalLoading().startLoading();
    const promise = new Promise<void>((resolve, reject) => {
      if (profile.value == null) {
        const watcher = watch(
          () => profile.value,
          newValue => {
            if (newValue) {
              resolve();
              watcher();
            }
          },
          { immediate: true }
        );
      } else {
        resolve();
      }
    });

    await promise;
    useGlobalLoading().stopLoading();
    next();
  } else {
    next();
  }
});

function validateClaims(token: string) {
  // decode the user token
  if (!token) return '';
  const userClaims = CommonHelper.decodeJsonWebToken(token);
  let errorMessage = undefined;

  if (!userClaims || !userClaims.vat) {
    errorMessage = 'Du mangler vat claim i dit token';
  }

  if (
    userClaims &&
    userClaims.vat &&
    !profile.value.isAuthorityExisting && // cvr in the vat claim does not exist in the organisation table
    (!userClaims.roles || (!userClaims.roles.includes(IOT_ADMIN) && !userClaims.roles.includes(IOT_AUTHORITY)))
  ) {
    errorMessage = 'Du mangler fornødne roller for at bruge dette site eller din organisation findes ikke i databasen';
  }

  return errorMessage;
}

router.beforeEach(async (to, from, next) => {
  if (profile.value != null && to.meta?.roleRequirements != null) {
    const token = useOidc().user.value?.access_token ?? '';
    const errorMessage = validateClaims(token);
    if (errorMessage) {
      next(false);
      useNavigateTo401(router, errorMessage);
      return;
    }

    // If user is logged in, then we know above hook ensures profile is downloaded, so we want to check if he can access page.
    if (!profile.value.isAdmin && !profile.value.isAuthority && !isReadOnly.value) {
      next(false);
      useNavigateTo401(
        router,
        'Du er ikke blevet tildelt de nødvendige roller for at få adgang til systemet. Kontakt venligst administrator hvis du mener dette er en fejl. Du kan se dine roller ved at klikke <a href="/claims">her</a>.'
      );
    } else if (!profile.value.isAuthorityExisting) {
      next(false);
      useNavigateTo401(
        router,
        `Din myndigheds CVR ${profile.value.cvr} er ikke oprettet i systemet. Kontakt venligst administrator hvis du mener dette er en fejl.`
      );
    } else if (!profile.value.isAuthorityActive) {
      next(false);
      useNavigateTo401(
        router,
        `Din myndigheds CVR ${profile.value.cvr} er deaktiveret i systemet. Kontakt venligst administrator hvis du mener dette er en fejl.`
      );
    } else {
      const fulfilled = to.meta.roleRequirements.some((role: string) => roles.value?.includes(role) ?? false);
      if (!fulfilled && !isReadOnly.value) return next({ path: '/401' });
    }
  }

  if (from.path == '/' && to.meta?.needsParent) {
    next(false);
    const name = to.matched[to.matched.length - 2].name;
    // This is for view device by device-id direct-link
    // the params is unble to pass because of the loggin redirection, use query instead
    if (name === 'Enheder' && to.params.deviceId) {
      await router.push({
        name: name,
        query: to.params
      });
    } else {
      await router.push({
        name: name,
        params: to.params
      });
      await router.push({ name: to.name ?? undefined, params: to.params });
    }
  } else {
    next();
  }
});

router.onError(err => {
  return Promise.reject(err);
});

export default router;
